import type { OnInit, Signal } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxAddress, OnyxRoutePoint } from '@onyx/angular';
import {
  OnyxInputLabelComponent,
  OnyxLinkComponent,
  onyxTextareaLengthValidator,
  onyxTextFieldLengthValidator,
} from '@onyx/angular';
import { omit } from 'lodash';
import { map } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';
import { ValidationHelper } from '../../../../../common/helpers/validation.helper';
import { OrderPointCategory } from '../../../common/enums/order-point-category';
import { OrderPointType } from '../../../common/enums/order-point-type';
import { OrderHelper } from '../../../common/helpers/order.helper';
import type { OrderFormGood } from '../../../common/interfaces/order-form-good';
import { OrderPointBaseFormComponent } from '../order-point-form/order-point-base-form/order-point-base-form.component';
import { OrderPointInformationFormComponent } from '../order-point-form/order-point-information-form/order-point-information-form.component';
import { OrderUnloadingPointFormGoodComponent } from './order-unloading-point-form-good/order-unloading-point-form-good.component';
import type { OrderUnloadingPointGoodFormGroup } from './order-unloading-point-form-good/order-unloading-point-good-form/order-unloading-point-good-form.component';
import { OrderUnloadingPointGoodFormComponent } from './order-unloading-point-form-good/order-unloading-point-good-form/order-unloading-point-good-form.component';

export type OrderUnloadingPointFormGroup = ReturnType<
  typeof OrderUnloadingPointFormComponent.buildForm
>;
export type OrderUnloadingPointForm = ReturnType<
  OrderUnloadingPointFormGroup['getRawValue']
>;

@Component({
  selector: 'app-order-unloading-point-form',
  imports: [
    OrderPointBaseFormComponent,
    OnyxInputLabelComponent,
    TranslatePipe,
    OnyxLinkComponent,
    OrderUnloadingPointFormGoodComponent,
    OrderPointInformationFormComponent,
  ],
  templateUrl: './order-unloading-point-form.component.html',
  styleUrl: './order-unloading-point-form.component.scss',
})
export class OrderUnloadingPointFormComponent implements OnInit {
  private readonly injector = inject(Injector);
  private readonly fb = inject(NonNullableFormBuilder);

  public readonly form = input.required<OrderUnloadingPointFormGroup>();
  public readonly goods = input.required<OrderFormGood[]>();
  public readonly disabled = input.required<boolean>();
  public readonly isCompleted = input.required<boolean>();

  protected readonly I18N = 'orders.orderForm.points.unloadingPoint';

  protected hasAllGoodsAvailable = computed(() => {
    const activeGoods = this.activeGoods();
    if (!activeGoods.length) return false;

    return activeGoods.every(
      (good) =>
        good.availableQuantity === OrderHelper.getGoodQuantity(good.good),
    );
  });
  protected hasAnyGoodsAvailable = computed(() =>
    this.activeGoods().some((good) => good.availableQuantity > 0),
  );

  protected goodForms = computed(() => {
    this.formValue();
    return new Map(
      this.form().controls.goods.controls.map((control) => [
        control.controls.uuid.value,
        control,
      ]),
    );
  });
  protected hasEmptyGoods = computed(
    () => !this.formValue().goods.length && this.form().touched,
  );

  private activeGoods = computed(() =>
    this.goods().filter(
      (good) =>
        OrderHelper.getGoodQuantity(good.good) && good.availableQuantity,
    ),
  );
  private goodsFormArray = computed(() => this.form().controls.goods);
  private formValue!: Signal<OrderUnloadingPointForm>;

  public ngOnInit(): void {
    if (this.disabled()) {
      ValidationHelper.disableControls(
        Object.values(omit(this.form().controls, 'goods')),
        { reset: false },
      );
    }

    this.formValue = toSignal(
      this.form().valueChanges.pipe(map(() => this.form().getRawValue())),
      {
        initialValue: this.form().getRawValue(),
        injector: this.injector,
      },
    );

    effect(
      () => {
        const availableGoods = new Set(
          this.goods().map((good) => good.good.uuid),
        );
        for (const form of this.goodsFormArray().controls) {
          const uuid = form.controls.uuid.value;
          if (!availableGoods.has(uuid)) this.removeGood(uuid);
        }
      },
      { injector: this.injector },
    );
  }

  protected unloadActiveGoods(): void {
    for (const good of this.activeGoods()) {
      const form = this.goodForms().get(good.good.uuid);
      if (!form) {
        this.addGood(good);
        continue;
      }

      const quantityControl = form.controls.quantity;
      quantityControl.setValue(
        (quantityControl.value || 0) + good.availableQuantity,
      );
    }
  }

  protected addGood(good: OrderFormGood): void {
    const uuid = good.good.uuid;
    if (this.goodForms().has(uuid)) return;

    this.goodsFormArray().push(
      OrderUnloadingPointGoodFormComponent.buildForm(
        this.fb,
        uuid,
        good.availableQuantity,
      ),
    );
  }

  protected removeGood(uuid: string): void {
    if (!this.goodForms().has(uuid)) return;

    const index = this.goodsFormArray().controls.findIndex(
      (control) => control.controls.uuid.value === uuid,
    );
    this.goodsFormArray().removeAt(index);
  }

  public static buildForm(
    fb: NonNullableFormBuilder,
    timeWindowsCount = 1,
    goodsCount = 0,
  ) {
    return fb.group({
      uuid: fb.control(uuidv7()),
      category: fb.control(OrderPointCategory.UNLOADING as const),
      type: fb.control(OrderPointType.UNLOADING as const),
      address: fb.control<OnyxAddress | null>(null, [Validators.required]),
      referenceNumber: fb.control<string | null>(null, [
        onyxTextFieldLengthValidator,
      ]),
      serviceTime: fb.control<number | null>(null, [Validators.required]),
      driverNote: fb.control<string | null>(null, [
        onyxTextareaLengthValidator,
      ]),
      timeWindows: OrderPointBaseFormComponent.buildTimeWindowsForm(
        fb,
        timeWindowsCount,
      ),
      goods: fb.array<OrderUnloadingPointGoodFormGroup>(
        Array.from({ length: goodsCount }).map(() =>
          OrderUnloadingPointGoodFormComponent.buildForm(fb, '', 0),
        ),
        [Validators.required],
      ),
      route: fb.control<OnyxRoutePoint | null>(null),
    });
  }
}
